// EmailLoginScreen.tsx
import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "../../common/hooks/useTheme";
import { selectIsOnline } from "../../store/slices/networkSlice";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { EmailStage, LegalityScreens } from "../../common/types/enums";
import { useLegalityModal } from "../../common/components/legalityModal/useLegalityModal";
import { useEmailLogin } from "./useEmailLogin";
import { MobileHeader } from "../../common/components/mobileHeader/MobileHeader";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import Button from "../../common/components/button/Button";
import Loader from "../../common/components/loader/Loader";
import styles from "./EmailLoginScreen.module.scss";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";

export default function EmailLoginScreen() {
  const { theme } = useTheme();
  const isOnline = useSelector(selectIsOnline);
  const currentCluster = useSelector(selectCurrentCluster);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const {
    stage,
    loading,
    message,
    registerMessage,
    isLogin,
    legalityChecked,
    email,
    code,
    timer,
    setCode,
    requestCode,
    verifyCode,
    register,
    resendCode,
    toggleLegality,
    setStage,
    isValidEmail,
  } = useEmailLogin();

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted);
      inputRefs.current[5]?.focus();
    }
  };

  const [localEmail, setLocalEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const { open } = useLegalityModal();

  const handleEmailChange = (value: string) => {
    setLocalEmail(value);
    if (value && !isValidEmail(value)) {
      setEmailError("Используйте почту в зоне .ru или .рф");
    } else {
      setEmailError("");
    }
  };

  const handleRequestCode = () => {
    if (!isValidEmail(localEmail)) {
      setEmailError("Используйте почту в зоне .ru или .рф");
      return;
    }
    setEmailError("");
    requestCode(localEmail);
  };

  const handleCodeChange = (text: string, index: number) => {
    const newCode = code.split("");
    newCode[index] = text;
    setCode(newCode.join(""));
    if (text && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const checkBoxTitle = (
    <div className={styles.linksContainer}>
      <span className={styles.toggleLabel}>Согласие с </span>
      <button
        className={styles.link}
        onClick={() =>
          open(
            currentCluster?.id!,
            currentCluster?.name,
            LegalityScreens.PERSONAL_DATA_AGREEMENT,
          )
        }
      >
        соглашением об обработке персональных данных
      </button>
      <button
        className={styles.link}
        onClick={() =>
          open(
            currentCluster?.id!,
            currentCluster?.name,
            LegalityScreens.USER_AGREEMENT,
          )
        }
      >
        пользовательским соглашением
      </button>
      <button
        className={styles.link}
        onClick={() =>
          open(
            currentCluster?.id!,
            currentCluster?.name,
            LegalityScreens.PRIVACY_POLICY,
          )
        }
      >
        политикой в отношении обработки персональных данных
      </button>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loaderContainer}>
          <Loader />
          {message && <p className={styles.messageText}>{message}</p>}
        </div>
      );
    }

    switch (stage) {
      case EmailStage.emailInput:
        return (
          <>
            <h1 className={styles.title}>Вход по email</h1>
            <p className={styles.subtitle}>
              Введите email для получения кода подтверждения
            </p>
            <div className={styles.inputContainer}>
              <input
                className={`${styles.input} ${emailError ? styles.inputError : ""}`}
                placeholder="example@mail.ru"
                value={localEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                type="email"
              />
              {<div className={styles.errorText}>{emailError}</div>}
            </div>
            <div className={styles.buttonContainer}>
              <Button
                title={
                  isOnline ? "Получить код" : "Для авторизации нужен интернет"
                }
                onClick={handleRequestCode}
                variant="primary"
                disabled={!isOnline || !localEmail || !isValidEmail(localEmail)}
                fullWidth
              />
            </div>
            <p className={styles.infoText}>
              После авторизации вы сможете просматривать историю заказов,
              получать персональные предложения
            </p>
          </>
        );

      case EmailStage.codeInput:
        return (
          <>
            <h1 className={styles.title}>Подтверждение входа</h1>
            <p className={styles.subtitle}>
              {registerMessage ||
                `Код отправлен на ${email}. Если письмо не пришло, проверьте папку Спам`}
            </p>
            {isLogin && (
              <div className={styles.codeContainer} onPaste={handlePaste}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      if (el) inputRefs.current[i] = el;
                    }}
                    className={styles.codeInput}
                    value={code[i] || ""}
                    onChange={(e) => handleCodeChange(e.target.value, i)}
                    onKeyDown={(e) => handleKeyPress(e, i)}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                  />
                ))}
              </div>
            )}
            <div className={styles.resendContainer}>
              <span className={styles.resendText}>Не пришел код?</span>
              {timer > 0 ? (
                <span className={styles.timerText}>{timer} сек</span>
              ) : (
                <button className={styles.resendButton} onClick={resendCode}>
                  Отправить еще раз
                </button>
              )}
            </div>
            {!isLogin && (
              <div className={styles.toggleContainer}>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={legalityChecked}
                    onChange={toggleLegality}
                  />
                  <span className={styles.slider} />
                </label>
                {checkBoxTitle}
              </div>
            )}
            <div className={styles.buttonContainer}>
              {!isLogin ? (
                <Button
                  title="Зарегистрироваться"
                  onClick={register}
                  variant="primary"
                  disabled={!isOnline || code.length !== 6 || !legalityChecked}
                  fullWidth
                />
              ) : (
                <Button
                  title="Отправить"
                  onClick={() => verifyCode(code)}
                  variant="primary"
                  disabled={!isOnline || code.length !== 6}
                  fullWidth
                />
              )}
            </div>
            {message && <p className={styles.messageText}>{message}</p>}
          </>
        );

      case EmailStage.error:
        return (
          <>
            <h1 className={styles.title}>Ошибка</h1>
            <p className={styles.subtitle}>
              {message || "Что-то пошло не так"}
            </p>
            <div className={styles.buttonContainer}>
              <Button
                title="Попробовать снова"
                onClick={() => setStage(EmailStage.emailInput)}
                variant="primary"
                fullWidth
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta />
      <div className={styles.mobileOnly}>
        <MobileHeader title="Вход по email" />
      </div>
      <div className={styles.desktopOnly}>
        <ProductHeader
          title="Вход по email"
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
      </div>
      <div className={styles.content}>{renderContent()}</div>
    </div>
  );
}
